import { Button, Toggle } from "~/components/ui";
import { useState } from "react";
import Label from "../form/Label";
import type { InsertNotificationProperties } from "~/types/database.types";
import ResultsModal from "../product/ResultsModal";
import HappyPersonIcon from "~/assets/HappyPersonIcon";
import CloudErrorIcon from "~/assets/CloudErrorIcon";
import { notificationManagementText } from "./notificationManagementText";
import { useLanguage } from "~/hooks/useLanguage";

interface ManageNotificationsProps {
  userNotificationProperties: InsertNotificationProperties;
}

const ManageNotifications = ({
  userNotificationProperties,
}: ManageNotificationsProps) => {
  const { lang } = useLanguage();
  const [enabled, setEnabled] = useState(
    userNotificationProperties?.is_on ?? false
  );
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<{
    header: string;
    details: string;
    actionButtonText: string;
    topIcon: React.ComponentType<{ className?: string }>;
  } | null>(null);

  const [subToggles, setSubToggles] = useState({
    monthly_updates: userNotificationProperties?.monthly_updates ?? false,
    insights: userNotificationProperties?.insights ?? false,
    masleka_updates: userNotificationProperties?.masleka_updates ?? false,
    announcements: userNotificationProperties?.announcements ?? true,
    push_enabled: userNotificationProperties?.push_enabled ?? true,
  });

  const userId = userNotificationProperties?.user_id;

  const handleMainToggle = (value: boolean) => {
    setEnabled(value);
    setSubToggles({
      monthly_updates: value,
      insights: value,
      masleka_updates: value,
      announcements: value,
      push_enabled: value,
    });
  };

  const handleSubToggle = (key: keyof typeof subToggles, value: boolean) => {
    setSubToggles((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();
    try {
      const response = await fetch(`/notificationProperties/${userId}`, {
        method: "POST",
        body: JSON.stringify({
          user_id: userId,
          is_on: enabled,
          monthly_updates: subToggles.monthly_updates,
          insights: subToggles.insights,
          masleka_updates: subToggles.masleka_updates,
          announcements: subToggles.announcements,
          push_enabled: subToggles.push_enabled,
        }),
      });

      setResults(
        response.status === 200
          ? {
              ...notificationManagementText[lang].results.success,
              topIcon: HappyPersonIcon,
            }
          : {
              ...notificationManagementText[lang].results.error,
              topIcon: CloudErrorIcon,
            }
      );
      setShowResults(true);
    } catch (error) {
      setResults({
        ...notificationManagementText[lang].results.error,
        topIcon: CloudErrorIcon,
      });
      setShowResults(true);
    }
  };

  return (
    <div dir={lang === "HEB" ? "rtl" : "ltr"} className="mx-auto p-4">
      <div className="m-auto flex flex-col mobile:w-full mobile:flex-wrap">
        <div className="m-auto flex flex-row justify-between self-center sm:w-[600px]">
          <h1 className="m-auto text-4xl tracking-tighter text-menu mobile:mt-1 mobile:text-2xl">
            {notificationManagementText[lang].title}
          </h1>
        </div>
        <div className="w-max-[700px] mx-auto w-[700px] mobile:w-full">
          <div className="mb-2 mobile:flex-col">
            <div className="flex flex-wrap gap-10 rounded-md bg-surface shadow-md mobile:h-fit mobile:bg-transparent mobile:shadow-none">
              <div className="w-full items-end gap-x-[103px] gap-y-[30px] mobile:w-full mobile:gap-x-5 mobile:gap-y-5">
                <div className="p-3">
                  <div className="mt-8 flex flex-col gap-4 mobile:mb-16">
                    {/* Main Toggle */}
                    <div className="mb-8 flex gap-[30px]">
                      <div className="flex w-full items-center justify-between">
                        <Label
                          label={
                            notificationManagementText[lang].mainToggle.label
                          }
                          className="ms-2 w-[300px] font-bold"
                        />
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={enabled}
                            onChange={handleMainToggle}
                            aria-label={
                              notificationManagementText[lang].mainToggle.label
                            }
                          />
                          <Label
                            label={
                              enabled
                                ? notificationManagementText[lang].mainToggle
                                    .yes
                                : notificationManagementText[lang].mainToggle.no
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sub Toggles */}
                    <div className="me-8 flex flex-col gap-4">
                      {/* Monthly Financial Update */}
                      <div className="flex w-full items-center justify-between">
                        <Label
                          label={
                            notificationManagementText[lang].subToggles
                              .monthly_updates
                          }
                          className="ms-2 w-[400px]"
                        />
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={subToggles.monthly_updates}
                            onChange={(value) =>
                              handleSubToggle("monthly_updates", value)
                            }
                            disabled={!enabled}
                            aria-label={
                              notificationManagementText[lang].subToggles
                                .monthly_updates
                            }
                          />
                          <Label
                            label={
                              subToggles.monthly_updates
                                ? notificationManagementText[lang].mainToggle
                                    .yes
                                : notificationManagementText[lang].mainToggle.no
                            }
                          />
                        </div>
                      </div>

                      {/* New Insights */}
                      <div className="flex w-full items-center justify-between">
                        <Label
                          label={
                            notificationManagementText[lang].subToggles.insights
                          }
                          className="ms-2 w-[400px]"
                        />
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={subToggles.insights}
                            onChange={(value) =>
                              handleSubToggle("insights", value)
                            }
                            disabled={!enabled}
                            aria-label={
                              notificationManagementText[lang].subToggles
                                .insights
                            }
                          />
                          <Label
                            label={
                              subToggles.insights
                                ? notificationManagementText[lang].mainToggle
                                    .yes
                                : notificationManagementText[lang].mainToggle.no
                            }
                          />
                        </div>
                      </div>

                      {/* Masleka Updates */}
                      <div className="flex w-full items-center justify-between">
                        <Label
                          label={
                            notificationManagementText[lang].subToggles
                              .masleka_updates
                          }
                          className="ms-2 w-[400px]"
                        />
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={subToggles.masleka_updates}
                            onChange={(value) =>
                              handleSubToggle("masleka_updates", value)
                            }
                            disabled={!enabled}
                            aria-label={
                              notificationManagementText[lang].subToggles
                                .masleka_updates
                            }
                          />
                          <Label
                            label={
                              subToggles.masleka_updates
                                ? notificationManagementText[lang].mainToggle
                                    .yes
                                : notificationManagementText[lang].mainToggle.no
                            }
                          />
                        </div>
                      </div>

                      {/* Product announcements */}
                      <div className="flex w-full items-center justify-between">
                        <Label
                          label={
                            notificationManagementText[lang].subToggles
                              .announcements
                          }
                          className="ms-2 w-[400px]"
                        />
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={subToggles.announcements}
                            onChange={(value) =>
                              handleSubToggle("announcements", value)
                            }
                            disabled={!enabled}
                            aria-label={
                              notificationManagementText[lang].subToggles
                                .announcements
                            }
                          />
                          <Label
                            label={
                              subToggles.announcements
                                ? notificationManagementText[lang].mainToggle
                                    .yes
                                : notificationManagementText[lang].mainToggle.no
                            }
                          />
                        </div>
                      </div>

                      <div className="flex w-full items-center justify-between">
                        <Label
                          label={
                            notificationManagementText[lang].subToggles
                              .push_enabled
                          }
                          className="ms-2 w-[400px]"
                        />
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={subToggles.push_enabled}
                            onChange={(value) =>
                              handleSubToggle("push_enabled", value)
                            }
                            disabled={!enabled}
                            aria-label={
                              notificationManagementText[lang].subToggles
                                .push_enabled
                            }
                          />
                          <Label
                            label={
                              subToggles.push_enabled
                                ? notificationManagementText[lang].mainToggle
                                    .yes
                                : notificationManagementText[lang].mainToggle.no
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex w-full justify-center">
          <Button className="mobile:px-16" onClick={handleSubmit}>
            {notificationManagementText[lang].saveButton}
          </Button>
        </div>
      </div>
      {results && (
        <ResultsModal
          isOpen={showResults}
          closeResultModal={() => setShowResults(false)}
          modalContent={results}
          actionButtonCallback={() => setShowResults(false)}
        />
      )}
    </div>
  );
};

export default ManageNotifications;
